/**
 * `/play/:pin` — el móvil del alumno, de la sala de espera al final de la partida.
 *
 * **Aquí no hay censo.** No es que se filtre al pintar: es que el servidor no lo manda. El alumno
 * recibe cuántos hay conectados y cómo se le ve a él, y nada más. La lista con nombres reales vive
 * solo en la pantalla del profesor (principio III, §12.3).
 *
 * **La respuesta se confirma al pulsar, sin esperar al servidor** (RF-013), y se retira con su
 * motivo si el servidor la rechaza. En la wifi de un instituto, esperar medio segundo a que
 * confirmen hace que la mitad de la clase pulse dos veces creyendo que no ha funcionado.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import {
  checkAlias,
  isValidPin,
  MAX_ALIAS_LENGTH,
  type SessionProfile,
  type StudentLeaderboardView,
  type StudentLobbyView,
  type StudentQuestionView,
  type StudentRevealView,
} from '@clica/shared';
import { useGame } from '../game/use-game.js';
import { useClock, useSegundosHasta, type ClockReading } from '../game/use-clock.js';
import type { GameSocket } from '../game/socket.js';
import { EstadoConexion } from '../components/estado-conexion.js';
import { Cronometro } from '../components/cronometro.js';
import { BotonesRespuesta } from '../components/botones-respuesta.js';
import { buzz } from '../game/sound.js';
import { ImagenPregunta } from '../components/imagen-pregunta.js';
import { ContenidoMarkdown } from '../markdown/contenido.js';
import { Boton } from '../components/sistema/boton.js';
import { Rotulo } from '../components/sistema/rotulo.js';
import { Marco } from '../components/sistema/marco.js';
import { Glifo } from '../components/sistema/glifo.js';
import { useAvisos, useHuecoDeAvisos } from '../components/sistema/avisos.js';
import { MOTIVOS_DE_APODO, useErroresComoAvisos } from '../game/usar-errores.js';

export function PantallaJugar({ profile }: { profile: SessionProfile }): React.JSX.Element {
  const { t } = useTranslation();
  const { pin = '' } = useParams();
  const navigate = useNavigate();
  const { state, socket } = useGame({ pin });
  const clock = useClock(state.anchor);

  /*
   * **Un PIN que no existe no lleva a una pantalla de error: lleva de vuelta al campo.**
   *
   * Antes el error se quedaba pegado en `state.error` para siempre y la sala no llegaba nunca:
   * el alumno acababa en una pantalla con un párrafo rojo, sin salida y sin forma de reintentar,
   * en el primer minuto de su primera partida. Ahora vuelve exactamente a donde tecleó —que en su
   * panel es lo primero que hay— y el motivo se lo cuenta un aviso, que además se va solo.
   */
  useErroresComoAvisos(state.error, (key) => {
    // Los tres rechazos de entrada devuelven al campo. `domainNotAllowed` es el más fácil de
    // olvidar y el que peor caería: al rechazado no se le cierra el socket —el veto es de esta
    // sala, no de la plataforma—, así que sin esto se quedaba en `/play/:pin` con la sala en
    // nulo, mirando una pantalla vacía en cuanto el aviso se fuera. Exactamente la pantalla
    // muerta que este cambio dice haber quitado.
    if (
      key === 'game.error.pinNotFound' ||
      key === 'game.error.quizNotPlayable' ||
      key === 'game.error.domainNotAllowed'
    ) {
      void navigate('/', { replace: true });
    }
  });

  const enSala = state.phase === 'lobby';

  // En una pregunta, lo último de la columna son las opciones C y D: los avisos se colocan por
  // encima en vez de taparlas. En la sala no hay nada fijo al pie.
  useHuecoDeAvisos(enSala ? '0px' : '3.5rem');
  const lobby = state.lobby?.view === 'student' ? state.lobby : null;
  const question = state.question?.view === 'student' ? state.question : null;
  const reveal = state.reveal?.view === 'student' ? state.reveal : null;
  const clasificacion = state.leaderboard?.view === 'student' ? state.leaderboard : null;

  if (!isValidPin(pin)) {
    return (
      <Marco profile={profile} minima>
        <main id="contenido" className="mx-auto max-w-md p-6">
          <p role="alert" className="text-ko text-lead font-display font-bold">
            {t('game.join.invalid')}
          </p>
          <Boton variante="primaria" className="mt-6" onClick={() => void navigate('/')}>
            {t('game.join.title')}
          </Boton>
        </main>
      </Marco>
    );
  }

  // Una sesión desplazada no se reintenta sola: dos clientes reconectando se expulsarían el uno al
  // otro sin fin. Se le ofrece volver, y decide la persona.
  if (state.replaced || state.closeCode === 4001) {
    return (
      <Marco profile={profile} minima>
        <main
          id="contenido"
          className="mx-auto flex max-w-md flex-col justify-center p-6"
          style={{ minHeight: 'calc(100dvh - 5rem)' }}
        >
          <h1 className="font-display text-h2 font-extrabold text-balance">
            {t('game.connection.replaced')}
          </h1>
          <p className="text-body text-text-dim mt-3">{t('game.connection.replacedHelp')}</p>
          <Boton
            variante="primaria"
            ancho
            className="mt-6"
            onClick={() => window.location.reload()}
          >
            {t('game.connection.retry')}
          </Boton>
        </main>
      </Marco>
    );
  }

  return (
    <Marco profile={profile} minima>
      <main
        id="contenido"
        className="mx-auto flex max-w-md flex-col p-4"
        style={{ minHeight: 'calc(100dvh - 5rem)' }}
      >
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <EstadoConexion status={state.status} />
        </div>

        {state.phase === 'lobby' ? (
          <SalaDeEspera lobby={lobby} pin={pin} socket={socket} />
        ) : state.phase === 'countdown' ? (
          <CuentaAtras startsAt={state.countdownStartsAt} />
        ) : state.phase === 'ended' ? (
          <Final score={state.score} clasificacion={clasificacion} />
        ) : question === null ? (
          <p role="status" className="mt-10 text-center">
            {t('common.loading')}
          </p>
        ) : (
          <Pregunta
            question={question}
            clock={clock}
            frozen={state.frozen}
            hintMd={state.hintMd}
            chosen={state.myOptionId}
            reveal={reveal}
            clasificacion={clasificacion}
            respondidas={state.answered}
            totalGente={state.total}
            onChoose={(optionId) => {
              socket?.send({ type: 'answer', questionId: question.questionId, optionId });
              // Lo único que hace el móvil del alumno además de dibujar (§0.6). Si el navegador no
              // la tiene, no pasa nada: la confirmación ya está en pantalla.
              buzz();
            }}
          />
        )}
      </main>
    </Marco>
  );
}

function SalaDeEspera({
  lobby,
  pin,
  socket,
}: {
  lobby: StudentLobbyView | null;
  pin: string;
  socket: GameSocket | null;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  if (lobby === null) return null;

  return (
    <div className="escena flex flex-1 flex-col">
      <div className="mt-8 flex items-center gap-3">
        {/*
          El punto que late.

          Una sala de espera sin nada que se mueva no se distingue de una sala colgada, y es la
          primera pantalla que ve un alumno con el móvil en la mano. Late despacio y solo sobre
          `transform`, así que no roba atención ni toca ningún texto.
        */}
        <span aria-hidden="true" className="latido bg-ok size-3 shrink-0 rounded-full" />
        <h1 className="font-display text-h2 font-extrabold text-balance">
          {t('game.lobby.title')}
        </h1>
      </div>
      <p className="text-body text-text-dim mt-3">{t('game.lobby.waiting')}</p>

      {/*
        Cómo se te ve, con la caja de verdad.

        El nombre real se recortaba a veinte caracteres y llegaba partido aquí, que es el único
        sitio donde se le enseña a su propia dueña. Ahora llega entero; si no cabe, lo recorta el
        CSS, que además deja el nombre completo para el lector de pantalla.
      */}
      <p className="rounded-card border-line bg-surface mt-6 border-2 px-4 py-3">
        <Rotulo className="block">{t('game.lobby.youAre')}</Rotulo>
        <span className="font-display text-h3 mt-1 block font-bold break-words">
          {lobby.you.label}
        </span>
      </p>

      <p className="text-body text-text-dim mt-4 flex items-center gap-2" aria-live="polite">
        <Glifo nombre="lleno" size={14} className="text-ok" />
        {t('game.lobby.connected', { count: lobby.connectedCount })}
      </p>

      {lobby.allowAlias ? (
        <FormularioAlias onSubmit={(alias) => socket?.send({ type: 'join', pin, alias })} />
      ) : null}
    </div>
  );
}

/**
 * La cuenta atrás.
 *
 * Eran dos renglones quietos durante tres segundos, que es exactamente lo contrario de una cuenta
 * atrás: no se sabe cuánto queda ni si la pantalla sigue viva. Ahora hay una diana que respira al
 * ritmo del `--dur-tick`, que es el segundo real del sistema.
 *
 * **No cuenta números**: el reloj lo lleva el servidor y aquí no hay anclaje todavía, así que
 * inventarse un 3-2-1 en el cliente sería el cliente decidiendo el tiempo, que es justo lo que el
 * principio I prohíbe. Lo que se anima es la espera, no la cuenta.
 */
function CuentaAtras({ startsAt }: { startsAt: string | null }): React.JSX.Element {
  const { t } = useTranslation();
  const segundos = useSegundosHasta(startsAt);

  return (
    <div className="escena flex flex-1 flex-col items-center justify-center gap-6 text-center">
      {/*
       * **La cifra, no un rótulo quieto.**
       *
       * Eran tres segundos de dos renglones sin moverse, que es lo contrario de una cuenta atrás:
       * no se sabe cuánto queda ni si la pantalla sigue viva. El dato estaba en el cliente desde
       * el principio —`countdownStartsAt`, que manda el servidor— y no lo leía nadie.
       *
       * La `key` es lo que hace que la animación se reinicie con cada cifra: sin ella React
       * reutiliza el nodo, la animación no vuelve a arrancar y el número cambia de golpe.
       */}
      {segundos === null ? (
        <span
          aria-hidden="true"
          className="latido border-edge bg-hivis flex size-32 items-center justify-center rounded-full border-4"
        >
          <Glifo nombre="reanudar" size={56} className="text-on-hivis ml-2" />
        </span>
      ) : (
        <span
          key={segundos}
          aria-hidden="true"
          className="cifra font-display text-tick text-edge leading-none font-extrabold tabular-nums"
        >
          {segundos}
        </span>
      )}
      {/* `assertive` porque interrumpe a propósito: la partida va a empezar (§15). */}
      <h1 role="status" aria-live="assertive" className="font-display text-h1 font-extrabold">
        {t('game.countdown.title')}
      </h1>
      <p className="text-lead text-text-dim">{t('game.countdown.lead')}</p>
    </div>
  );
}

function Final({
  score,
  clasificacion,
}: {
  score: number;
  clasificacion: StudentLeaderboardView | null;
}): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="escena flex flex-1 flex-col items-center justify-center text-center">
      <h1 className="font-display text-h1 font-extrabold">{t('game.ended.title')}</h1>
      {clasificacion === null ? null : (
        /* El puesto entra con el sobreimpulso del veredicto: es el dato que se está esperando. */
        <p className="veredicto font-display text-score mt-6 font-extrabold tabular-nums">
          {t('game.rank.position', { rank: clasificacion.you.rank })}
          <span className="text-body text-text-dim block font-normal">
            {t('game.rank.of', { total: clasificacion.total })}
          </span>
        </p>
      )}
      <p className="text-lead font-display mt-4 font-bold tabular-nums">
        {t('game.ended.yourScore', { score })}
      </p>

      {/* Salida. Esta pantalla terminaba en la puntuación, sin un solo enlace: quien acababa la
          partida se quedaba mirando su puesto sin forma de ir a ninguna parte. */}
      <Boton variante="primaria" className="mt-8" onClick={() => void navigate('/')}>
        {t('game.lobby.leave')}
      </Boton>
    </div>
  );
}

/**
 * La posición y los vecinos.
 *
 * **«A 180 puntos de quien va delante» es jugable; «vas decimotercero» no lo es.** Es lo que
 * sostiene la atención de quien no va ganando, que es la mayoría de la clase.
 */
function Posicion({ clasificacion }: { clasificacion: StudentLeaderboardView }): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <div className="entra border-line bg-surface rounded-card mt-3 border-2 px-3 py-2 text-center">
      <p className="font-display text-h3 font-bold tabular-nums">
        {t('game.rank.position', { rank: clasificacion.you.rank })}
        <span className="text-text-dim text-body ml-2 font-normal">
          {t('game.rank.of', { total: clasificacion.total })}
        </span>
      </p>
      <p className="text-text-dim text-micro mt-1">
        {clasificacion.ahead === null
          ? t('game.rank.leading')
          : t('game.rank.ahead', {
              gap: clasificacion.ahead.gap,
              label: clasificacion.ahead.label,
            })}
      </p>
      {clasificacion.behind === null ? null : (
        <p className="text-text-dim text-micro">
          {t('game.rank.behind', {
            gap: clasificacion.behind.gap,
            label: clasificacion.behind.label,
          })}
        </p>
      )}
    </div>
  );
}

function Pregunta({
  question,
  clock,
  frozen,
  hintMd,
  chosen,
  reveal,
  clasificacion,
  respondidas,
  totalGente,
  onChoose,
}: {
  question: StudentQuestionView;
  clock: ClockReading;
  frozen: 'paused' | 'hostAway' | null;
  hintMd: string | null;
  chosen: string | null;
  reveal: StudentRevealView | null;
  clasificacion: StudentLeaderboardView | null;
  respondidas: number;
  totalGente: number;
  onChoose: (optionId: string) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const revelada = reveal !== null;

  return (
    <div className="flex flex-1 flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-text-dim text-sm">
          {t('game.question.of', { index: question.index + 1, total: question.total })}
        </p>
        <Cronometro reading={clock} frozen={frozen} size={72} />
      </div>

      {frozen !== null ? (
        <p role="status" className="bg-surface rounded-lg px-3 py-2 text-center text-sm">
          {t(`game.${frozen}.lead`)}
        </p>
      ) : null}

      {/*
        El enunciado se muestra también en el móvil, con altura acotada para no comerse los botones.
        Depender solo del proyector da por hecho que todo el mundo lo ve, y quien se sienta al fondo
        o tiene un reflejo encima no lo ve.
      */}
      {/* `assertive` como en el proyector: que se abra una pregunta interrumpe a propósito.
          El del móvil no tenía ni `role` ni `aria-live`, así que quien no ve la pantalla no se
          enteraba de que había empezado. */}
      <div aria-live="assertive">
        <ContenidoMarkdown
          source={question.promptMd}
          // De 14 px en una caja recortada a 20-30 px sin recorte: el enunciado es lo que hay que
          // leer para responder, y quien se sienta al fondo o tiene un reflejo no ve el proyector.
          className="prose-clica text-stem"
        />
      </div>

      {question.image === null ? null : (
        <div className="flex justify-center">
          <ImagenPregunta
            path={question.image.path}
            alt={question.image.alt}
            width={question.image.width}
            height={question.image.height}
            className="max-h-32 w-auto rounded-lg object-contain"
          />
        </div>
      )}

      {hintMd !== null ? (
        <div role="status" className="border-edge bg-surface rounded-chip border-l-4 px-3 py-2">
          <p className="text-text text-xs font-semibold uppercase">{t('game.question.hint')}</p>
          <ContenidoMarkdown source={hintMd} className="prose-clica text-sm" />
        </div>
      ) : null}

      {revelada ? <Resultado reveal={reveal} /> : null}
      {revelada && clasificacion !== null ? <Posicion clasificacion={clasificacion} /> : null}

      {/* Sin envoltorio: la cuadrícula es hija directa de la columna flexible y crece ella misma.
          Con un `div` en medio, el `height: 100%` de la lista no encontraba altura de referencia y
          las cuatro opciones se quedaban en 223 px de los 609 disponibles. */}
      <BotonesRespuesta
        options={question.options}
        chosen={chosen}
        correctOptionId={reveal?.correctOptionId ?? null}
        disabled={chosen !== null || revelada || frozen !== null}
        onChoose={onChoose}
      />

      <p role="status" className="text-body text-text-dim min-h-5 text-center">
        {revelada
          ? null
          : chosen !== null
            ? // Tras responder, lo único que se decía era «no se puede cambiar». El recuento ya
              // llegaba en cada tic del servidor y no lo pintaba nadie: saber que faltan seis
              // personas convierte la espera en algo, en vez de en un vacío.
              totalGente === 0
              ? t('game.question.locked')
              : t('game.question.lockedCount', { answered: respondidas, total: totalGente })
            : t('game.question.chooseOne')}
      </p>
    </div>
  );
}

/** El resultado, con símbolo y palabra además de color (principio IV). */
function Resultado({ reveal }: { reveal: StudentRevealView }): React.JSX.Element {
  const { t } = useTranslation();
  const you = reveal.you;

  const acierto = you.correct;
  const sinResponder = you.optionId === null;

  return (
    <div
      // `assertive`: el resultado es lo que la persona está esperando en ese instante (§15).
      role="status"
      aria-live="assertive"
      /*
       * **La inundación del veredicto, que estaba definida y sin usar.**
       *
       * `--color-field-ok` y `--color-field-ko` llevaban desde el principio en el sistema, con
       * sus ratios medidos y documentados, y no los pintaba nadie: el resultado era una línea de
       * texto verde o roja sobre el fondo de siempre. Un fondo entero es lo que hace que se lea
       * de un vistazo, con el móvil a medio camino del bolsillo, sin buscar dónde mirar.
       *
       * El glifo y la palabra siguen estando: el color es el tercer canal, nunca el único.
       */
      className={[
        'veredicto rounded-card border-2 px-4 py-3 text-center',
        acierto
          ? 'bg-field-ok border-ok'
          : sinResponder
            ? 'bg-surface border-line'
            : 'bg-field-ko border-ko',
      ].join(' ')}
    >
      <p className="text-verdict font-display flex items-center justify-center gap-3 font-extrabold">
        <Glifo
          nombre={acierto ? 'correcto' : sinResponder ? 'vacio' : 'incorrecto'}
          size={32}
          className={acierto ? 'text-ok' : sinResponder ? 'text-text-dim' : 'text-ko'}
        />
        <span className={acierto ? 'text-ok' : sinResponder ? 'text-text-dim' : 'text-ko'}>
          {acierto
            ? t('game.reveal.correct')
            : sinResponder
              ? t('game.reveal.noAnswer')
              : t('game.reveal.wrong')}
        </span>
      </p>
      <p className="text-text text-body mt-2 font-bold tabular-nums">
        {t('game.reveal.points', { points: you.points })} ·{' '}
        {t('game.reveal.total', { score: you.totalPoints })}
        {you.streak > 1 ? ` · ${t('game.reveal.streak', { count: you.streak })}` : ''}
      </p>
      {you.hintApplied ? (
        <p className="text-text-dim text-micro mt-1">{t('game.reveal.hintApplied')}</p>
      ) : null}
    </div>
  );
}

/**
 * El apodo se comprueba **también aquí**, aunque el servidor lo vuelva a comprobar.
 *
 * No es duplicar la regla: la regla vive una sola vez, en `packages/shared`, y las dos partes
 * llaman a la misma función (principio II). Lo del navegador es cortesía —decir «esto no vale»
 * antes de enviarlo—, y la decisión sigue siendo del servidor.
 */

/**
 * El apodo.
 *
 * Aquí estaban las dos cosas que el sistema de avisos vino a arreglar. Una, un renglón reservado
 * al pie del formulario —`min-h-6`, siempre ocupando sitio— que se rellenaba con «Enviado.». Y
 * otra, el motivo del rechazo pintado como párrafo entre el campo y el botón, que al aparecer
 * empujaba el botón bajo el pulgar de quien iba a pulsarlo.
 *
 * Las dos son sucesos: pasan, se cuentan y se van. Ahora las cuenta un aviso al pie, que no mueve
 * nada de sitio y desaparece solo.
 *
 * El campo sigue marcándose como inválido (`aria-invalid`), porque eso sí es un estado.
 */
function FormularioAlias({ onSubmit }: { onSubmit: (alias: string) => void }): React.JSX.Element {
  const { t } = useTranslation();
  const avisos = useAvisos();
  const [alias, setAlias] = useState('');
  const [invalido, setInvalido] = useState(false);

  return (
    <form
      className="border-line mt-8 flex flex-col gap-3 border-t-2 pt-6"
      onSubmit={(event) => {
        event.preventDefault();
        const check = checkAlias(alias);
        if (!check.ok) {
          setInvalido(true);
          avisos.fallo(t(MOTIVOS_DE_APODO[check.reason]));
          return;
        }
        setInvalido(false);
        // «Enviado», no «guardado»: el servidor puede rechazarlo por repetido o por impropio, y
        // dar por hecho el sí antes de preguntarlo es prometer algo que no se sabe. Si lo rechaza,
        // llega un `game.error.aliasRejected` que se cuenta por el mismo sitio.
        avisos.correcto(t('game.lobby.aliasSent'));
        onSubmit(check.alias);
      }}
    >
      <Rotulo htmlFor="alias">{t('game.lobby.aliasLabel')}</Rotulo>
      <input
        id="alias"
        value={alias}
        maxLength={MAX_ALIAS_LENGTH * 2}
        onChange={(event) => {
          setAlias(event.target.value);
          setInvalido(false);
        }}
        aria-describedby="alias-help"
        aria-invalid={invalido}
        className={[
          'text-body rounded-chip bg-surface ease-hard min-h-11 border-2 px-3 py-2.5',
          'transition-colors duration-(--dur-state)',
          invalido ? 'border-ko' : 'border-line',
        ].join(' ')}
      />
      <p id="alias-help" className="text-micro text-text-dim">
        {t('game.lobby.aliasHelp')}
      </p>
      <Boton type="submit" variante="secundaria" className="self-start">
        {t('game.lobby.aliasSubmit')}
      </Boton>
    </form>
  );
}
